import { jsonApiRequest, nextPageCursor, unwrapList, unwrapOne } from './jsonApi.js';

/**
 * Client for cloud-discount-service (cds).
 *
 * Routes come from the generated `DiscountManagementApi`:
 *   /api/v2/discounts/merchant/{merchant}/discounts[/{discountId}]
 *
 * ⚠️ `{merchant}` is the merchant **urlname**, not the display name. cds resolves it with
 * `MerchantMapper.findByName` → `WHERE name = ?`, but that `name` column *contains husky's
 * urlname*: sync_db_job's `HuskyMerchantHandler` does `setName(s.getUrlname())` and puts the
 * human-readable name in `display_name`. Passing the display name yields a raw
 * `NoSuchElementException` (500), not a clean 404. Same key for core-api's `/data/*`.
 */

/** JSON:API resource type cds requires on every request body (`data.type`). */
const RESOURCE_TYPE = 'discounts';

const base = merchantUrlname =>
    `${process.env.REACT_APP_DISCOUNT_API_URL}/api/v2/discounts/merchant/${encodeURIComponent(merchantUrlname)}/discounts`;

/**
 * Largest `page[size]` cds accepts (openapi `maximum: 500`). The portal filters and sorts the
 * whole list client-side, so it asks for the biggest pages to keep the round trips down.
 */
export const LIST_PAGE_SIZE = 500;

/**
 * Hard stop for the page loop, far above any real merchant (MAX_LIST_PAGES × LIST_PAGE_SIZE
 * discounts). It only exists so a server bug that keeps returning a `next` link cannot hang the page.
 */
const MAX_LIST_PAGES = 100;

// Shared with the cloud-core-api client; re-exported so existing imports keep working.
export { nextPageCursor };

/**
 * Every discount for a merchant, as flat `{id, ...attributes}` rows.
 *
 * cds pages the list with a keyset cursor in its default sort (`-createdAt`, id as tie-break) and
 * JSON:API `links.next`. No `sort` or `filter[...]` is sent: the portal wants every discount and
 * sorts locally.
 * This follows `next` until it is null and returns all rows, because the discounts page needs
 * the complete set: it searches, sorts and paginates locally, and maps targets per rule.
 *
 * Includes paused discounts: cds filters on `deleted_at IS NULL` only, so `active = false` rows
 * are returned and map to legacy `Status: 2`.
 * @param {string} merchantUrlname
 * @returns {Promise<Array<object>>}
 */
export async function listDiscounts(merchantUrlname) {
    const rows = [];
    const seen = new Set();
    let cursor = null;

    for (let pageNo = 0; pageNo < MAX_LIST_PAGES; pageNo++) {
        const url = `${base(merchantUrlname)}?page[size]=${LIST_PAGE_SIZE}`
            + (cursor ? `&page[cursor]=${encodeURIComponent(cursor)}` : '');
        const envelope = await jsonApiRequest(url);
        rows.push(...unwrapList(envelope));

        cursor = nextPageCursor(envelope?.links?.next);
        if (!cursor) {
            return rows;
        }
        if (seen.has(cursor)) {
            throw new Error('Discount service returned the same page cursor twice; the list is incomplete.');
        }
        seen.add(cursor);
    }
    throw new Error(`Discount service returned more than ${MAX_LIST_PAGES} pages; the list is incomplete.`);
}

/**
 * Create one discount from a cds `DiscountAttributes` object.
 * @param {string} merchantUrlname
 * @param {object} attributes
 * @returns {Promise<object|null>} the created `{id, ...attributes}`
 */
export async function createDiscount(merchantUrlname, attributes) {
    const body = await jsonApiRequest(base(merchantUrlname), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { type: RESOURCE_TYPE, attributes } }),
    });
    return unwrapOne(body);
}

/**
 * Metadata-only PATCH. cds accepts just `active, name, description, displayName,
 * displayDescription, displayImageUrl, providerId, reference, translations` — no amounts,
 * schedule or targets. That is why a full edit is modelled as create-then-delete.
 * cds requires `data.id` and answers 409 if it differs from the id in the URL.
 * @param {string} merchantUrlname
 * @param {string} discountId
 * @param {object} attributes
 * @returns {Promise<object>}
 */
export function patchDiscountMetadata(merchantUrlname, discountId, attributes) {
    return jsonApiRequest(`${base(merchantUrlname)}/${discountId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { type: RESOURCE_TYPE, id: discountId, attributes } }),
    });
}

/**
 * Soft-delete one discount row (cds sets `deleted_at`). cds answers 204 with no body.
 * @param {string} merchantUrlname
 * @param {string} discountId
 * @returns {Promise<undefined>}
 */
export function deleteDiscount(merchantUrlname, discountId) {
    return jsonApiRequest(`${base(merchantUrlname)}/${discountId}`, { method: 'DELETE' });
}
